import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'

import { TMarketYoyGrowthExpectationMarket, TMarketYoyGrowthExpectationRtm } from '../api/types'
import {
  IMarketYoyGrowthCategory,
  IMarketYoyGrowthEditEntry,
  IMarketYoyGrowthExpectation,
  IMarketYoyGrowthGeoMarket,
  IMarketYoyGrowthPlanning,
  TGrowthField,
  TMarketYoyGrowthEdits,
} from '../model/types'

import { getMarketYoyGrowthExpectationKey } from './getMarketYoyGrowthExpectationKey'

export interface IMarketYoyGrowthEditTarget {
  /** `null` targets every market (a Worldwide cell edit). */
  market: TMarketYoyGrowthExpectationMarket | null
  offering: ProgramTypes
  /** `null` targets every category of the offering (a group-row cell edit). */
  productCategory: string | null
  /** The currently viewed RTM — edits never cross into the other RTM's records. */
  rtm: TMarketYoyGrowthExpectationRtm
  growthField: TGrowthField
  value: number
}

/**
 * Fans a single cell edit out to all records the cell aggregates (the target
 * market or every market when `market` is `null`, and all categories of the
 * offering when `productCategory` is `null`), scoped to `target.rtm` only,
 * and returns the next edits map. Edits equal to the fetched value are
 * pruned, so an empty map always means "nothing to save".
 *
 * Combinations with no backing record yet (a brand-new offering/category on
 * an empty market) are synthesized from `categories` + `planning` as phantom
 * entries carrying a `base` record — these are never pruned, since there's no
 * fetched value to revert to except discarding all edits.
 */
export const buildMarketYoyGrowthEdits = (
  records: IMarketYoyGrowthExpectation[],
  categories: IMarketYoyGrowthCategory[],
  geoMarkets: IMarketYoyGrowthGeoMarket[],
  planning: IMarketYoyGrowthPlanning | null,
  edits: TMarketYoyGrowthEdits,
  target: IMarketYoyGrowthEditTarget
): TMarketYoyGrowthEdits => {
  const { market, offering, productCategory, rtm, growthField, value } = target
  const next: TMarketYoyGrowthEdits = { ...edits }
  const recordsByKey = new Map(
    records.map((record) => [getMarketYoyGrowthExpectationKey(record), record])
  )

  records.forEach((record) => {
    if (
      record.offering !== offering ||
      record.rtm !== rtm ||
      (productCategory !== null && record.productCategory !== productCategory) ||
      (market !== null && record.market !== market)
    ) {
      return
    }

    const key = getMarketYoyGrowthExpectationKey(record)
    const patch: IMarketYoyGrowthEditEntry = { ...(next[key] ?? {}) }

    if (record[growthField] === value) {
      delete patch[growthField]
    } else {
      patch[growthField] = value
    }

    if (Object.keys(patch).length) {
      next[key] = patch
    } else {
      delete next[key]
    }
  })

  const targetCategories = categories.filter(
    (category) =>
      category.offering === offering &&
      category.rtm === rtm &&
      (productCategory === null || category.category === productCategory)
  )
  const allMarkets = Array.from(new Set(geoMarkets.map((geoMarket) => geoMarket.short)))
  const targetMarkets = market === null ? allMarkets : [market]

  targetCategories.forEach((category) => {
    targetMarkets.forEach((targetMarket) => {
      const identity = {
        fYear: planning!.fYear,
        rtm: category.rtm,
        market: targetMarket,
        offering,
        productCategory: category.category,
      }
      const key = getMarketYoyGrowthExpectationKey(identity)

      if (recordsByKey.has(key)) {
        return
      }

      const existingEntry = next[key]
      const base: IMarketYoyGrowthExpectation = existingEntry?.base ?? {
        ...identity,
        planningEntryId: planning!.entryId,
        yoyGrowth: 0,
        sobYoyGrowth: 0,
        isExpanded: false,
      }

      next[key] = { ...(existingEntry ?? {}), base, [growthField]: value }
    })
  })

  return next
}
